/**
 * Bero API client — shared by resident app (native + web) and later the guard app.
 * Token storage is injected per platform (expo-secure-store native, localStorage web).
 * All endpoints return camelCase JSON per docs/API_CONTRACT.md.
 */

export type TokenStore = {
  get: (key: string) => Promise<string | null>;
  set: (key: string, value: string) => Promise<void>;
  del: (key: string) => Promise<void>;
};

export type ApiError = { status: number; error: string; pendingApproval?: boolean };

export type SessionUser = {
  id: number; societyId: number | null; email: string; phone: string | null;
  fullName: string; role: 'RESIDENT' | 'GUARD' | 'FRONTDESK' | 'COMMITTEE' | 'SUB_ADMIN' | 'MASTER_ADMIN' | 'PLATFORM_ADMIN';
  residentType: 'OWNER' | 'TENANT' | 'FAMILY';
  isApproved: boolean; isActive: boolean; avatarUrl: string | null; language: string;
  flat?: { id: number; code: string; bhk: string };
};

const ACCESS = 'bero.access';
const REFRESH = 'bero.refresh';

/**
 * Extra per-request headers an app-level module can inject without this
 * package depending on that module (kept dependency-free). Used by the
 * resident app's acting-society plumbing to attach X-Bero-Society while a
 * Bero platform staffer is operating a society's console on its behalf —
 * see src/lib/acting-society.ts.
 */
let headerProvider: (() => Record<string, string> | null) | null = null;
export function setHeaderProvider(fn: () => Record<string, string> | null) {
  headerProvider = fn;
}

export class BeroClient {
  constructor(
    private baseUrl: string,
    private store: TokenStore,
    private onSignedOut?: () => void,
  ) {}

  /* ---------- token plumbing ---------- */
  private async request<T>(path: string, init: RequestInit = {}, retry = true): Promise<T> {
    const access = await this.store.get(ACCESS);
    const res = await fetch(`${this.baseUrl}${path}`, {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        ...(access ? { Authorization: `Bearer ${access}` } : {}),
        ...(headerProvider?.() ?? {}),
        ...(init.headers ?? {}),
      },
    });
    // /api/auth/login and /api/auth/refresh returning 401 is their normal
    // "bad credentials" / "bad refresh token" failure mode, not a stale-token
    // event — the session-expiry interceptor must not run for them, or the
    // caller's own catch (e.g. login.tsx) never sees the real ApiError.
    const isSessionExemptEndpoint = path === '/api/auth/login' || path === '/api/auth/refresh'
      || path === '/api/auth/verify-code' || path === '/api/auth/google' || path === '/api/auth/apple';
    if (res.status === 401 && retry && !isSessionExemptEndpoint) {
      const refreshed = await this.tryRefresh();
      if (refreshed) return this.request<T>(path, init, false);
      await this.clearSession();
      this.onSignedOut?.();
    }
    const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    if (!res.ok) {
      throw { status: res.status, error: (body.error as string) ?? 'Request failed', ...body } as ApiError;
    }
    return body as T;
  }

  private async tryRefresh(): Promise<boolean> {
    const refresh = await this.store.get(REFRESH);
    if (!refresh) return false;
    try {
      const res = await fetch(`${this.baseUrl}/api/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: refresh }),
      });
      if (!res.ok) return false;
      const body = (await res.json()) as { accessToken: string; refreshToken: string };
      await this.store.set(ACCESS, body.accessToken);
      await this.store.set(REFRESH, body.refreshToken);
      return true;
    } catch {
      return false;
    }
  }

  async clearSession() {
    await this.store.del(ACCESS);
    await this.store.del(REFRESH);
  }

  async hasSession(): Promise<boolean> {
    return (await this.store.get(REFRESH)) !== null;
  }

  /* ---------- verbs ---------- */
  get<T>(path: string) { return this.request<T>(path); }
  post<T>(path: string, body?: unknown) { return this.request<T>(path, { method: 'POST', body: JSON.stringify(body ?? {}) }); }
  patch<T>(path: string, body?: unknown) { return this.request<T>(path, { method: 'PATCH', body: JSON.stringify(body ?? {}) }); }
  delete<T>(path: string) { return this.request<T>(path, { method: 'DELETE' }); }

  /* ---------- auth ---------- */
  async login(email: string, password: string) {
    const body = await this.post<{ accessToken: string; refreshToken: string; user: SessionUser }>(
      '/api/auth/login', { email, password },
    );
    await this.store.set(ACCESS, body.accessToken);
    await this.store.set(REFRESH, body.refreshToken);
    return body.user;
  }

  async signup(input: {
    email: string; password: string; fullName: string;
    // Society/flat are optional now (multi-society, MyGate model): account-first
    // signup omits them and the person adds a flat afterwards; the old
    // society-first onboarding still passes them to create the first membership.
    societyId?: number;
    phone?: string; residentType?: string; flatConfigId?: number;
  }) {
    return this.post<{ user: SessionUser; pendingApproval: boolean }>('/api/auth/signup', input);
  }

  async logout() {
    const refresh = await this.store.get(REFRESH);
    try { await this.post('/api/auth/logout', { refreshToken: refresh }); } catch { /* best effort */ }
    await this.clearSession();
  }

  me() { return this.get<{ user: SessionUser }>('/api/auth/me'); }

  /** Passwordless step 1: ask the server to email a 6-digit code. Always 200
   *  (no account enumeration). */
  async requestCode(identifier: string) {
    return this.post<{ ok: boolean }>('/api/auth/request-code', { identifier });
  }

  /** Passwordless step 2: verify the code and sign in. Persists the token pair
   *  under the same keys login() uses. On a brand-new email the account is
   *  created (society-agnostic) with fullName + phone; newUser=true. */
  async verifyCode(identifier: string, code: string, fullName?: string, phone?: string) {
    const body = await this.post<{ accessToken: string; refreshToken: string; user: SessionUser; newUser: boolean }>(
      '/api/auth/verify-code',
      { identifier, code, ...(fullName ? { fullName } : {}), ...(phone ? { phone } : {}) },
    );
    await this.store.set(ACCESS, body.accessToken);
    await this.store.set(REFRESH, body.refreshToken);
    return { user: body.user, newUser: body.newUser };
  }

  /** Social sign-in: server verifies a Google ID token, then signs in. */
  async google(idToken: string) {
    const body = await this.post<{ accessToken: string; refreshToken: string; user: SessionUser; newUser: boolean }>(
      '/api/auth/google', { idToken },
    );
    await this.store.set(ACCESS, body.accessToken);
    await this.store.set(REFRESH, body.refreshToken);
    return { user: body.user, newUser: body.newUser };
  }

  /** Social sign-in: server verifies an Apple identity token, then signs in. */
  async apple(payload: { identityToken: string; nonce?: string; fullName?: string }) {
    const body = await this.post<{ accessToken: string; refreshToken: string; user: SessionUser; newUser: boolean }>(
      '/api/auth/apple', payload,
    );
    await this.store.set(ACCESS, body.accessToken);
    await this.store.set(REFRESH, body.refreshToken);
    return { user: body.user, newUser: body.newUser };
  }

  /**
   * Adopt a token pair minted OUTSIDE the normal /api/auth/login flow (e.g.
   * POST /api/platform/claim-owner, which mints tokens the same way
   * auth.py's issue_tokens() does after an owner sets their password).
   * Persists both tokens under the exact same store keys login() uses, so
   * the rest of the client (refresh, hasSession, request auth header) can't
   * tell the difference. Caller must still make the SessionProvider pick up
   * the new user — call useSession().refresh() right after this.
   */
  async adoptTokens(tokens: { accessToken: string; refreshToken: string; user: SessionUser }) {
    await this.store.set(ACCESS, tokens.accessToken);
    await this.store.set(REFRESH, tokens.refreshToken);
    return tokens.user;
  }
}
